import { z } from 'zod/v4'

export type GoogleAdsToken = {
	access_token: string
	expires_in: number
	expire_time: number
	scope: 'https://www.googleapis.com/auth/adwords'
	token_type: 'Bearer'
}

export type GoogleAdsResourceNames = {
	resourceNames: string[]
}

export type GoogleAdsApiResponse<T> = {
	results: T[]
	fieldMask: string
}

export type Customer = {
	id: string
	descriptiveName: string
	resourceName: string
	manager: boolean
	otherDetails: any[]
	customerClients?: Customer[]
}

export type GoogleAdsCampaign = {
	id: string
	name: string
}

export type GeoPoint = {
	latitude: number
	longitude: number
	radius: number
	radiusUnits: string
}

export type GoogleAdsAddress = {
	streetAddress?: string
	streetAddress2?: string
	cityName?: string
	provinceCode?: string
	provinceName?: string
	postalCode?: string
	countryCode?: string
}

export type GeoTargetConstant = {
	canonicalName: string
	name: string
	id: string
	countryCode: string
	parentGeoTarget: string
	resourceName: string
	targetType: string
	status: string
}

export type GoogleAdsLocation = {
	geoTargetConstant: GeoTargetConstant
	locationSourceType?: string
	address: GoogleAdsAddress
	geoPoint: GeoPoint
	negative: boolean
	displayName: string
	resourceName: string
}

export type GoogleAdsRow = {
	campaignCriterion: CampaignCriterion
	campaign: {
		localCampaignSetting?: {
			locationSourceType?: string
		}
	}
	geoTargetConstant?: GeoTargetConstant
}

export type GAGeoPoint = {
	longitudeInMicroDegrees: number
	latitudeInMicroDegrees: number
}

export type RemoveOperation = {
	/**
	 * Resource name for the criterion to remove
	 */
	remove: string
}

export const CampaignCriterionValidator = z.object({
	resourceName: z.string().optional(),
	displayName: z.string().optional(),
	type: z.any().optional(),
	status: z.any().optional(),
	campaign: z.string(),
	criterionId: z.number().optional(),
	bidModifier: z.number().optional(),
	negative: z.boolean().optional(),
	location: z.any().optional(),
	proximity: z.any().optional(),
})

export type CampaignCriterion = z.infer<typeof CampaignCriterionValidator>

export type CriterionType =
	| 'UNSPECIFIED'
	| 'UNKNOWN'
	| 'KEYWORD'
	| 'PLACEMENT'
	| 'MOBILE_APP_CATEGORY'
	| 'MOBILE_APPLICATION'
	| 'DEVICE'
	| 'LOCATION'
	| 'LISTING_GROUP'
	| 'AD_SCHEDULE'
	| 'AGE_RANGE'
	| 'GENDER'
	| 'INCOME_RANGE'
	| 'PARENTAL_STATUS'
	| 'YOUTUBE_VIDEO'
	| 'YOUTUBE_CHANNEL'
	| 'USER_LIST'
	| 'PROXIMITY'
	| 'TOPIC'
	| 'LISTING_SCOPE'
	| 'LANGUAGE'
	| 'IP_BLOCK'
	| 'CONTENT_LABEL'
	| 'CARRIER'
	| 'USER_INTEREST'
	| 'WEBPAGE'
	| 'OPERATING_SYSTEM_VERSION'
	| 'APP_PAYMENT_MODEL'
	| 'MOBILE_DEVICE'
	| 'CUSTOM_AFFINITY'
	| 'CUSTOM_INTENT'
	| 'LOCATION_GROUP'
	| 'CUSTOM_AUDIENCE'
	| 'COMBINED_AUDIENCE'
	| 'KEYWORD_THEME'
	| 'AUDIENCE'
	| 'NEGATIVE_KEYWORD_LIST'
	| 'LOCAL_SERVICE_ID'
	| 'SEARCH_THEME'
	| 'BRAND'
	| 'BRAND_LIST'
	| 'LIFE_EVENT'

export type CampaignCriterionStatus = 'UNSPECIFIED' | 'ENABLED' | 'PAUSED' | 'REMOVED' | 'UNKNOWN'

export type LocationInfo = {
	/**
	 * The geotarget constant resource name
	 */
	geoTargetConstant: string
}

export type ProximityInfo = {
	geoPoint: GeoPointInfo
	radiusUnits: ProximityRadiusUnits
	address?: AddressInfo
	radius: number
}

export type GeoPointInfo = {
	longitudeInMicroDegrees: number
	latitudeInMicroDegrees: number
}

export type ProximityRadiusUnits = 'MILES' | 'KILOMETERS' | 'UNKNOWN' | 'UNSPECIFIED'

export type AddressInfo = {
	postalCode?: string
	provinceCode?: string
	countryCode?: string
	provinceName?: string
	streetAddress?: string
	streetAddress2?: string
	cityName?: string
}

export type UpdateOperation = {}

export type CampaignCriterionOperation =
	| {
			create: CampaignCriterion
	  }
	| { update: CampaignCriterion }
	| {
			/**
			 * Remove operation: A resource name for the removed criterion is expected, in this format:
			 * customers/{customer_id}/campaignCriteria/{campaign_id}~{criterion_id}
			 */
			remove: string
	  }

export enum ResponseContentType {
	UNSPECIFIED = 'UNSPECIFIED',
	RESOURCE_NAME_ONLY = 'RESOURCE_NAME_ONLY',
	MUTABLE_RESOURCE = 'MUTABLE_RESOURCE',
}

export type MutateGoogleAdsRequest = {
	mutateOperations: MutateOperation[]
	partialFailure?: boolean
	validateOnly?: boolean
	responseContentType?: ResponseContentType
}

export type MutateOperation = {
	campaignCriterionOperation: CampaignCriterionOperation
}

export type AccessibleGoogleAccount = {
	id: string
	descriptiveName: string
	manager: boolean
	resourceName: string
	customerClients: Omit<AccessibleGoogleAccount, 'customerClients'>[]
}
